import { useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DynamicTable from "./DynamicTable";
import { toast } from "react-toastify";
import { useTableHook, DynamicTableHelper } from "@workspace/common";
import { JOB_MANDATORY_OPTIONS, JOB_INITIAL_OPTIONS } from "../CandidateMappingConstants";
import { JOB_STAGE_IDS, JOB_STAGE_STATUS } from "../../../components/JobListing/JobListingConstants";
import { tagJob, fetchJobsToCandidateMapping, resetJobsToCandidateMapping } from "../../../store/actions";

const DynamicTableWrapper = ({ candidateData, jobId, expandedRow, closeTag }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tableConfig, setTableConfig] = useState([]);
  const [abortController, setAbortController] = useState(null);

  // Get the recommended jobs from the store
  const matchedJobs = useSelector((state) => state.JobReducer.matchedJobs);
  const isMatchedJobsLoading = useSelector((state) => state.JobReducer.loading);
  const jobsMatched = matchedJobs?.jobs;
  const jobsMatchedResult = matchedJobs?.jobsMatched?.results;
  const jobsMatchedData = jobsMatched?.map((job, index) => ({
    ...job,
    jobsMatchedResult: jobsMatchedResult[index]
  }));

  // Custom Render List
  const customRenderList = [
    {
      names: ["jobSubmissionData.jobTitle"],
      render: (data, opt) => (
        <Link
          className="text-custom-primary text-decoration-underline"
          onClick={() => {
            const win = window.open(
              `/jobs/${data?.id}/snapshot`,
              "_blank"
            );
            win.focus();
          }}
        >
          <span>{data?.jobSubmissionData?.jobTitle}</span>
        </Link>
      ),
    },
  ];

  // Generate Candidate Config
  const generateJobConfig = () => {
    return [
      // Indexing Column
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      ...customConfig,
      // Action Column
      {
        header: "Action",
        name: "action",
        sort: false,
        sortValue: "action",
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => (
          <div
            className="d-flex align-items-center justify-content-center cursor-pointer"
            onClick={() => handleTag(data)}
          >
            <span className="mdi mdi-check-circle-outline fs-4 text-true-primary"></span>
          </div>
        ),
      },
    ].filter((item) => item);
  };

  // Table Hooks
  const {
    pageRequestSet,
    pageInfo,
    customConfig,
    tableData,
  } = useTableHook(
    {
      page: 0,
      pageSize: 10,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
      allActive: true,
    },
    JOB_MANDATORY_OPTIONS,
    JOB_INITIAL_OPTIONS,
    customRenderList,
    generateJobConfig
  );

  // Fetch recommended jobs when offCanvasStatus / checked changes
  useEffect(() => {
    const newAbortController = new AbortController();

    if (expandedRow) {
      setAbortController(newAbortController);
      dispatch(
        fetchJobsToCandidateMapping(
          candidateData.id,
          jobId,
          newAbortController.signal,
        )
      );
    } else if (abortController) {
      abortController.abort();
      setAbortController(null);
      dispatch(resetJobsToCandidateMapping());
    }
    return () => {
      // Abort the request when the component unmounts or if tagOffcanvas changes to false
      newAbortController.abort();
      dispatch(resetJobsToCandidateMapping());
    };
  }, [expandedRow]);

  // Update the table config when customConfig changes
  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateJobConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig?.find(
          (oldItem) => oldItem?.name === item?.name
        );
        // If cannot find then continue
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateJobConfig(customConfig));
    }
  }, [customConfig, pageInfo, tableData]);

  // Handle Tag Candidate
  const handleTag = (data) => {
    const candidateId = candidateData?.id;
    const jobId = data?.id;
    const payload = {
      jobId: jobId,
      jobStageId: JOB_STAGE_IDS?.TAG,
      status: JOB_STAGE_STATUS?.COMPLETED,
      candidateId,
      stepName: "Profile",
      subStepName: "Tag",
    };
    try {
      dispatch(tagJob({ payload, navigate }));
      closeTag();
    } catch (error) {
      toast.error("Error tagging job.");
    }
  };

  return (
    <Row>
      <Col lg={12}>
        <div className="listjs-table">
          <DynamicTable
            data={jobsMatchedData}
            config={tableConfig}
            pageRequestSet={pageRequestSet}
            freezeHeader={true}
            isLoading={isMatchedJobsLoading}
            tableHeight="100%"
          />
        </div>
      </Col>
    </Row>
  );
};

export default DynamicTableWrapper;
